import { useCallback, useEffect, useRef, useState } from 'react'

import { useUpdateEffect } from 'react-use'

import type { FilterValue } from '@lib/types'

const thumbPixelSize = 26

type RangeSliderDropdownParams = {
	field: string
	min: {
		label: string
		value: number
	}
	max: {
		label: string
		value: number
	}
	filter?: FilterValue
	onChangeHandler: (min: number, max: number, filter?: FilterValue) => void
}

export default function RangeSliderDropdown({
	field,
	min,
	max,
	filter,
	onChangeHandler
}: RangeSliderDropdownParams) {
	// Toggle open
	const [open, toggle] = useState(false)

	// left and width are percentages, pixelOffset is to offset range thumbs
	const [trackStyle, setTrackStyle] = useState({
		left: 0,
		width: 100,
		pixelOffset: { min: 0, max: 0 }
	})

	// Minimum range value
	const [minVal, setMinVal] = useState(min?.value)
	// Maximum range value
	const [maxVal, setMaxVal] = useState(max?.value)

	const [isSliding, setIsSliding] = useState(false)

	// Input refs
	const minValRef = useRef<HTMLInputElement>(null)
	const maxValRef = useRef<HTMLInputElement>(null)

	const startSliding = () => setIsSliding(true)
	const stopSliding = () => setIsSliding(false)

	// Convert to percentage
	const getPercent = useCallback(
		(value: number) => {
			const percent = ((value - min.value) / (max.value - min.value)) * 100

			return parseFloat(percent.toFixed(4))
		},
		[min.value, max.value]
	)

	// Set width of the range to decrease from the left side
	useEffect(() => {
		if (maxValRef.current) {
			const minPercent = getPercent(minVal)
			const maxPercent = getPercent(Number(maxValRef.current.value))
			const minRemainder = (minPercent / 100) * thumbPixelSize
			const maxRemainder = ((100 - (maxPercent - minPercent)) / 100) * thumbPixelSize

			setTrackStyle((prev) => ({
				left: minPercent,
				width: maxPercent - minPercent,
				pixelOffset: {
					min: minRemainder,
					max: maxRemainder
				}
			}))
		}
	}, [minVal, getPercent])

	// Set width of the range to decrease from the right side
	useEffect(() => {
		if (minValRef.current) {
			const minPercent = getPercent(Number(minValRef.current.value))
			const maxPercent = getPercent(maxVal)
			const maxRemainder = ((100 - (maxPercent - minPercent)) / 100) * thumbPixelSize

			setTrackStyle((prev) => ({
				left: prev.left,
				width: maxPercent - minPercent,
				pixelOffset: {
					min: prev.pixelOffset.min,
					max: maxRemainder
				}
			}))
		}
	}, [maxVal, getPercent])

	/**
	 * pass range values to the external handler only when user 'stops sliding'
	 */
	useUpdateEffect(() => {
		if (!isSliding) {
			onChangeHandler(minVal, maxVal, filter)
		}
	}, [isSliding])

	// @TJ custom double thumb slider
	// not working in firefox ATM
	return (
		<div className="pt-2 pb-4">
			<button
				type="button"
				className="flex justify-between w-full py-1 font-medium"
				onClick={() => toggle((prev) => !prev)}
			>
				{field}
				<span className={`transition duration-300 ease-in-out ${open ? 'rotate-180' : 'rotate-0'}`}>
					<i className="text-gray-500 fa-regular fa-angle-down" />
				</span>
			</button>

			<div className={`px-2 ${open ? '' : 'hidden'}`}>
				<div className="w-full mt-4">
					<div className="flex justify-between px-1">
						<label htmlFor={`${field}-minimum-value`} className="text-xs">
							{minVal === min.value ? min.label : `$${minVal}`}
						</label>

						<label htmlFor={`${field}-maximum-value`} className="text-xs">
							{maxVal === max.value ? max.label : `$${maxVal}`}
						</label>
					</div>

					<div className="relative flex w-full mt-2">
						<input
							type="range"
							id={`${field}-minimum-value`}
							ref={minValRef}
							min={min.value}
							max={max.value}
							value={minVal}
							onChange={(e) =>
								Number(e.target.value) < Number(maxVal) ? setMinVal(Number(e.target.value)) : null
							}
							onMouseDown={startSliding}
							onMouseUp={stopSliding}
							className="refine-results-range-slider"
						/>

						<input
							type="range"
							id={`${field}-maximum-value`}
							ref={maxValRef}
							min={min?.value}
							max={max?.value}
							value={maxVal}
							onChange={(e) =>
								Number(e.target.value) > Number(minVal) ? setMaxVal(Number(e.target.value)) : null
							}
							onMouseDown={startSliding}
							onMouseUp={stopSliding}
							className="refine-results-range-slider"
						/>
					</div>

					{/* Visual range (what's shown on screen) */}
					<div className="relative z-10 w-full h-6 rounded-full bg-gray-200">
						<div
							className="absolute inset-y-0 h-full rounded-full bg-bbx-spark"
							style={{
								left: `calc(${trackStyle.left}% - ${trackStyle.pixelOffset.min}px)`,
								width: `calc(${trackStyle.width}% + ${trackStyle.pixelOffset.max}px)`
							}}
						/>
					</div>
				</div>
			</div>
		</div>
	)
}
